
import { useEffect, useState } from "react";
import { Cookie, ShieldCheck, X } from "lucide-react";

const COOKIE_CONSENT_KEY = "hasSeenModal";

export default function Cookies() {
  const [mounted, setMounted] = useState(false);
  const [pathname, setPathname] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setMounted(true);
    setPathname(window.location.pathname);
  }, []);

  useEffect(() => {
    if (!mounted || pathname !== "/") return;

    const hasSeenModal = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (hasSeenModal) return;

    const timer = window.setTimeout(() => {
      setIsOpen(true);
    }, 2000);

    return () => window.clearTimeout(timer);
  }, [mounted, pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    if (isClosing) return;

    setIsClosing(true);

    window.setTimeout(() => {
      localStorage.setItem(COOKIE_CONSENT_KEY, "true");
      setIsOpen(false);
      setIsClosing(false);
    }, 180);
  };

  const handleBackdropClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!mounted || !isOpen) return null;

  return (
    <div
      role="presentation"
      className={`
        fixed
        inset-0
        z-[9999]
        flex
        items-end
        justify-center
        bg-[#000C0F]/75
        px-3
        pb-3
        backdrop-blur-[2px]
        transition-opacity
        duration-200
        ${isClosing ? "opacity-0" : "opacity-100"}
      `}
      onMouseDown={handleBackdropClick}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        aria-describedby="cookie-modal-description"
        className={`
          relative
          w-full
          max-w-4xl
          overflow-hidden
          rounded-2xl
          border
          border-white/10
          bg-[#00363D]
          shadow-[0_24px_80px_rgba(0,0,0,0.45)]
          transition-all
          duration-200
          ease-out
          ${
            isClosing
              ? "translate-y-4 scale-[0.99] opacity-0"
              : "translate-y-0 scale-100 opacity-100"
          }
        `}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Fechar aviso sobre cookies"
          className="
            absolute
            right-4
            top-4
            z-10
            flex
            h-9
            w-9
            items-center
            justify-center
            rounded-full
            text-white/60
            transition-all
            duration-200
            hover:bg-white/10
            hover:text-white
            active:scale-95
            focus:outline-none
            focus:ring-2
            focus:ring-[#00CAD8]/60
            focus:ring-offset-2
            focus:ring-offset-[#00363D]
          "
        >
          <X
            size={18}
            strokeWidth={1.8}
            aria-hidden="true"
          />
        </button>

        {/* Header */}
        <div className="px-6 pb-2 pt-6 sm:px-8 sm:pt-7">
          <div className="flex w-full items-start gap-4 pr-8">
            <div
              className="
                flex
                h-12
                w-12
                shrink-0
                items-center
                justify-center
                rounded-2xl
                border
                border-[#00CAD8]/20
                bg-[#00CAD8]/10
              "
              aria-hidden="true"
            >
              <Cookie
                size={24}
                strokeWidth={1.7}
                className="text-[#00CAD8]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <span
                className="
                  text-[11px]
                  font-medium
                  uppercase
                  tracking-[0.2em]
                  text-[#00CAD8]/90
                "
              >
                Privacidade
              </span>

              <h2
                id="cookie-modal-title"
                className="
                  text-lg
                  font-semibold
                  tracking-tight
                  text-white
                  sm:text-xl
                "
              >
                Aviso sobre cookies
              </h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 sm:px-8 sm:py-5">
          <div className="flex flex-col gap-5">
            <p
              id="cookie-modal-description"
              className="
                max-w-3xl
                text-sm
                leading-7
                text-white/75
                sm:text-[15px]
              "
            >
              Utilizamos cookies para compreender como o site é utilizado e
              aprimorar sua experiência de navegação. Esses dados são usados
              para análises estatísticas e melhoria dos nossos serviços.
            </p>

            <div
              className="
                flex
                items-start
                gap-3
                rounded-xl
                border
                border-white/10
                bg-black/10
                px-4
                py-3.5
              "
            >
              <ShieldCheck
                size={19}
                strokeWidth={1.7}
                className="mt-0.5 shrink-0 text-[#00CAD8]"
                aria-hidden="true"
              />

              <p className="text-xs leading-5 text-white/60 sm:text-sm">
                Não utilizamos cookies para coletar diretamente dados pessoais.
                Ao continuar navegando, você concorda com a utilização dessas
                tecnologias para as finalidades informadas.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="
            flex
            flex-col
            gap-3
            border-t
            border-white/10
            px-6
            py-5
            sm:flex-row
            sm:items-center
            sm:justify-between
            sm:px-8
          "
        >
          <p
            className="
              text-center
              text-xs
              leading-5
              text-white/45
              sm:text-left
            "
          >
            Sua privacidade e segurança são importantes para nós.
          </p>

          <button
            type="button"
            onClick={handleClose}
            className="
              min-h-11
              min-w-[120px]
              rounded-full
              bg-[#00CAD8]
              px-7
              text-sm
              font-semibold
              text-[#00363D]
              shadow-[0_8px_24px_rgba(0,202,216,0.18)]
              transition-all
              duration-200
              hover:-translate-y-0.5
              hover:bg-[#18d7e4]
              hover:shadow-[0_12px_30px_rgba(0,202,216,0.25)]
              active:translate-y-0
              active:scale-[0.98]
              focus:outline-none
              focus:ring-2
              focus:ring-[#00CAD8]
              focus:ring-offset-2
              focus:ring-offset-[#00363D]
            "
          >
            Continuar
          </button>
        </div>
      </section>
    </div>
  );
}

